import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, Send, UserPlus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function TeamCard({ team, onChange }) {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/team/${team.id}/members`, { email: email.trim() });
      toast.success(`Access sent to ${email.trim()}`);
      setEmail("");
      onChange(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't add them"); }
    finally { setBusy(false); }
  };
  const resend = async (m) => {
    try { const { data } = await api.post(`/team/${team.id}/members/resend`, { email: m }); data.ok ? toast.success(`Resent to ${m}`) : toast.error("The email didn't send. Please try again shortly."); }
    catch { toast.error("Couldn't resend"); }
  };
  return (
    <div className="rounded-2xl border border-nm-primary/10 bg-nm-surface p-6 sm:p-8 nm-shadow-sm" data-testid="team-card">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-nm-neutral">Team license · {team.seats} seats</div>
          <div className="font-serif text-3xl text-nm-ink mt-1" data-testid="team-remaining">{team.remaining} {team.remaining === 1 ? "license" : "licenses"} left</div>
        </div>
        <div className="text-sm text-nm-neutral" data-testid="team-used">{team.used} of {team.seats} in use</div>
      </div>
      <div className="mt-4 h-2 w-full rounded-full bg-nm-primary/10 overflow-hidden">
        <div className="h-full bg-nm-primary" style={{ width: `${(team.used / team.seats) * 100}%` }} />
      </div>
      {team.remaining > 0 && (
        <form onSubmit={add} className="mt-6 flex flex-col sm:flex-row gap-3">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Teammate's email"
            data-testid="team-add-email" className="flex-1 rounded-full border border-nm-primary/20 bg-white px-5 py-3 text-sm outline-none focus:border-nm-primary" />
          <button type="submit" disabled={busy} data-testid="team-add-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-nm-coral px-6 py-3 text-sm font-semibold text-white hover:brightness-105 transition-[filter] duration-200 disabled:opacity-50">
            <UserPlus size={16} /> {busy ? "Adding…" : "Add & send access"}
          </button>
        </form>
      )}
      <ul className="mt-6 divide-y divide-nm-ink/[0.06]">
        <li className="py-3 flex items-center justify-between text-sm"><span className="text-nm-ink">{user?.email}</span><span className="text-nm-neutral">You</span></li>
        {team.members.map((m) => (
          <li key={m.email} className="py-3 flex items-center justify-between text-sm" data-testid="team-member-row">
            <span className="text-nm-ink">{m.email}{m.email_status === "failed" && <span className="ml-2 text-xs text-nm-coral">email didn't send</span>}</span>
            <button onClick={() => resend(m.email)} data-testid="team-resend-btn" className="inline-flex items-center gap-1.5 text-nm-primary hover:underline"><Send size={13} /> Resend access</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MyTeam() {
  const [teams, setTeams] = useState(null);
  const load = useCallback(() => api.get("/team").then((r) => setTeams(r.data.teams)).catch(() => setTeams([])), []);
  useEffect(() => { load(); }, [load]);
  const update = (t) => setTeams((prev) => prev.map((x) => (x.id === t.id ? t : x)));
  return (
    <div className="max-w-3xl" data-testid="my-team-page">
      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-nm-primary flex items-center gap-2"><Users size={14} /> My Team</div>
      <h1 className="font-serif text-4xl sm:text-5xl text-nm-ink mt-2 leading-none">Bring your team along.</h1>
      <p className="text-nm-neutral mt-4 leading-relaxed">Add a teammate's email and they'll get their own access to all three toolkits right away. You can fill your licenses any time.</p>
      <div className="mt-8 space-y-6">
        {teams?.map((t) => <TeamCard key={t.id} team={t} onChange={update} />)}
        {teams && teams.length === 0 && <p className="text-nm-neutral" data-testid="team-empty">You don't have a team license yet.</p>}
      </div>
    </div>
  );
}
